import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { InlineWidget } from 'react-calendly';
import { bookDemo } from '../../services/bookDemo';

export default function BookDemoQuiz({ steps, onImageChange }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepHistory, setStepHistory] = useState([]);
  const [data, setData] = useState({});
  const [tempInput, setTempInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const progressPercent = (currentStepIndex / (totalSteps - 1)) * 100;

  useEffect(() => {
    if (currentStep?.image && onImageChange) {
      onImageChange(currentStep.image);
    }
  }, [currentStepIndex, currentStep?.image, onImageChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDynamicTitle = (step) => {
    let title = step.title;

    if (step.dynamicTitle) {
      const { dependency, cases, contains } = step.dynamicTitle;
      const val = data[dependency];

      if (cases) {
        const caseTitle =
          (typeof val === 'string' && cases[val]) || cases.default;
        if (caseTitle) title = caseTitle;
      }

      if (contains && Array.isArray(val)) {
        const match = Object.keys(contains).find((key) =>
          val.some((v) => v.includes(key))
        );
        if (match) title = contains[match];
      }
    }

    return title.replace('{name}', data.name || '');
  };

  const handleNext = (newData = {}) => {
    const updatedData = { ...data, ...newData };
    setData(updatedData);
    setTempInput('');
    setStepHistory((prev) => [...prev, currentStepIndex]);

    let nextIndex = currentStepIndex + 1;

    while (nextIndex < steps.length) {
      const nextStep = steps[nextIndex];

      // Skip steps whose prerequisite condition is not met
      if (nextStep.condition) {
        const { field, value } = nextStep.condition;
        if (updatedData[field] !== value) {
          nextIndex++;
          continue;
        }
      }

      // Skip steps when a skipIf condition is met
      if (nextStep.skipIf) {
        const { field, includes } = nextStep.skipIf;
        const val = updatedData[field];
        if (Array.isArray(val) ? val.includes(includes) : val === includes) {
          nextIndex++;
          continue;
        }
      }

      break;
    }

    if (nextIndex >= steps.length) {
      // Quiz completed — submit to Pipedrive
      bookDemo(updatedData).catch((err) =>
        console.error('Failed to submit demo request:', err)
      );
    }

    setCurrentStepIndex(nextIndex);
  };

  const handleBack = () => {
    if (stepHistory.length === 0) return;
    const prevIndex = stepHistory[stepHistory.length - 1];
    setStepHistory((prev) => prev.slice(0, -1));
    setTempInput('');
    setDropdownOpen(false);
    setCurrentStepIndex(prevIndex);
  };

  const showBackButton =
    stepHistory.length > 0 &&
    currentStepIndex > 1 &&
    currentStep?.type !== 'success';

  const toggleMultiSelect = (field, value) => {
    const current = data[field] || [];
    if (current.includes(value)) {
      setData({ ...data, [field]: current.filter((i) => i !== value) });
    } else {
      setData({ ...data, [field]: [...current, value] });
    }
  };

  const isFieldsValid = (fields) => {
    return fields.every((f) => data[f.name]);
  };

  const ChevronDownIcon = () => (
    <svg
      width="10"
      height="6"
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`quiz__dropdown-chevron ${dropdownOpen ? 'quiz__dropdown-chevron--open' : ''}`}
    >
      <path d="M1 1L5 5L9 1" />
    </svg>
  );

  const CheckIcon = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const slideVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: 'tween', duration: 0.4, ease: 'easeOut' },
    },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } },
  };

  const renderStepContent = () => {
    if (!currentStep) return null;

    const title = getDynamicTitle(currentStep);

    switch (currentStep.type) {
      case 'welcome':
        return (
          <div className="quiz__welcome">
            <h1 className="quiz__welcome-title">{title}</h1>
            <p className="quiz__welcome-subtitle">{currentStep.subtitle}</p>
            <button
              type="button"
              onClick={() => handleNext()}
              className="quiz__button"
            >
              {currentStep.buttonLabel}
            </button>
          </div>
        );

      case 'input':
        return (
          <div className="quiz__step">
            <div className="quiz__header">
              <h2 className="quiz__title">{title}</h2>
              {currentStep.subtitle && (
                <p className="quiz__subtitle">{currentStep.subtitle}</p>
              )}
            </div>
            <input
              type="text"
              autoFocus
              className="quiz__input"
              placeholder={currentStep.placeholder}
              value={tempInput}
              onChange={(e) => setTempInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && tempInput && currentStep.field) {
                  handleNext({ [currentStep.field]: tempInput });
                }
              }}
            />
            <button
              type="button"
              disabled={!tempInput}
              onClick={() =>
                currentStep.field &&
                handleNext({ [currentStep.field]: tempInput })
              }
              className="quiz__button"
            >
              {currentStep.buttonLabel}
            </button>
          </div>
        );

      case 'inputs':
        return (
          <div className="quiz__step">
            <div className="quiz__header">
              <h2 className="quiz__title">{title}</h2>
              {currentStep.subtitle && (
                <p className="quiz__subtitle">{currentStep.subtitle}</p>
              )}
            </div>
            <div className="quiz__inputs">
              {currentStep.fields?.map((field) => (
                <input
                  key={field.name}
                  type={field.name === 'email' ? 'email' : 'text'}
                  className="quiz__input"
                  placeholder={field.placeholder}
                  value={data[field.name] || ''}
                  onChange={(e) =>
                    setData((prev) => ({ ...prev, [field.name]: e.target.value }))
                  }
                />
              ))}
            </div>
            <button
              type="button"
              disabled={!currentStep.fields || !isFieldsValid(currentStep.fields)}
              onClick={() => handleNext()}
              className="quiz__button"
            >
              {currentStep.buttonLabel}
            </button>
          </div>
        );

      case 'options':
        return (
          <div className="quiz__step">
            <div className="quiz__header">
              <h2 className="quiz__title">{title}</h2>
              {currentStep.subtitle && (
                <p className="quiz__subtitle">{currentStep.subtitle}</p>
              )}
            </div>
            <div className="quiz__options">
              {currentStep.options?.map((opt) => {
                const isSelected = currentStep.multiSelect
                  ? (data[currentStep.field] || []).includes(opt)
                  : data[currentStep.field] === opt;

                return (
                  <motion.button
                    key={opt}
                    type="button"
                    className={`quiz__option ${isSelected ? 'quiz__option--selected' : ''}`}
                    onClick={() => {
                      if (currentStep.multiSelect && currentStep.field) {
                        toggleMultiSelect(currentStep.field, opt);
                      } else if (currentStep.field) {
                        handleNext({ [currentStep.field]: opt });
                      }
                    }}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <span className="quiz__option-label">{opt}</span>
                    <span
                      className={`quiz__option-check ${isSelected ? 'quiz__option-check--selected' : ''}`}
                    >
                      {isSelected && <CheckIcon />}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            {currentStep.multiSelect ? (
              <button
                type="button"
                disabled={
                  !data[currentStep.field] ||
                  data[currentStep.field].length === 0
                }
                onClick={() => handleNext()}
                className="quiz__button"
              >
                {currentStep.buttonLabel}
              </button>
            ) : data[currentStep.field] ? (
              <button
                type="button"
                onClick={() => handleNext()}
                className="quiz__button"
              >
                Continue
              </button>
            ) : null}
          </div>
        );

      case 'dropdown_multi': {
        const selected = data[currentStep.field] || [];
        return (
          <div className="quiz__step">
            <div className="quiz__header">
              <h2 className="quiz__title">{title}</h2>
              {currentStep.subtitle && (
                <p className="quiz__subtitle">{currentStep.subtitle}</p>
              )}
            </div>
            <div className="quiz__dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="quiz__dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span className="quiz__dropdown-text">
                  {selected.length > 0
                    ? `${selected.length} selected`
                    : 'Select...'}
                </span>
                <ChevronDownIcon />
              </button>
              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    className="quiz__dropdown-menu"
                    initial={{ opacity: 0, y: -10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                  >
                    {currentStep.options?.map((opt) => {
                      const isActive = selected.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          className={`quiz__dropdown-item ${isActive ? 'quiz__dropdown-item--active' : ''}`}
                          onClick={() =>
                            currentStep.field &&
                            toggleMultiSelect(currentStep.field, opt)
                          }
                        >
                          <span>{opt}</span>
                          {isActive && <CheckIcon />}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
              {selected.length > 0 && (
                <div className="quiz__tags">
                  {selected.map((s) => (
                    <motion.span
                      key={s}
                      className="quiz__tag"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      {s}
                      <button
                        type="button"
                        onClick={() =>
                          currentStep.field &&
                          toggleMultiSelect(currentStep.field, s)
                        }
                        className="quiz__tag-remove"
                      >
                        ×
                      </button>
                    </motion.span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              disabled={selected.length === 0}
              onClick={() => handleNext()}
              className="quiz__button"
            >
              {currentStep.buttonLabel}
            </button>
          </div>
        );
      }

      case 'success': {
        const showCalendly =
          data.timeline === 'Immediately (within 1-2 weeks)' ||
          data.timeline === 'In the next 1–2 months';

        if (showCalendly) {
          return (
            <div className="quiz__calendly">
              <h2 className="quiz__title">Pick a time for your demo</h2>
              <p className="quiz__subtitle">
                Select a date and time that works best for you.
              </p>
              <div className="quiz__calendly-widget">
                <InlineWidget
                  url="https://calendly.com/ogiso-demo/30min?primary_color=ffba45"
                  prefill={{
                    name: data.name || '',
                    email: data.email || '',
                  }}
                  styles={{ height: '100%', width: '100%' }}
                />
              </div>
            </div>
          );
        }

        return (
          <div className="quiz__success">
            <motion.div
              className="quiz__success-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <span role="img" aria-label="celebration">🎉</span>
            </motion.div>
            <h2 className="quiz__success-title">{title}</h2>
            <p className="quiz__success-subtitle">{currentStep.subtitle}</p>
            <div className="quiz__summary">
              <p className="quiz__summary-label">Contact Information</p>
              <div className="quiz__summary-content">
                <div className="quiz__summary-row">
                  <span className="quiz__summary-key">Name</span>
                  <span className="quiz__summary-value">{data.name}</span>
                </div>
                {data.email && (
                  <div className="quiz__summary-row">
                    <span className="quiz__summary-key">Email</span>
                    <span className="quiz__summary-value">{data.email}</span>
                  </div>
                )}
                {data.phone && (
                  <div className="quiz__summary-row">
                    <span className="quiz__summary-key">Phone</span>
                    <span className="quiz__summary-value">{data.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }

      default:
        return <div>Unknown step type</div>;
    }
  };

  return (
    <div className="quiz">
      {showBackButton && (
        <button type="button" className="quiz__back" onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
      )}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep?.id || currentStepIndex}
          variants={slideVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="quiz__content"
        >
          {renderStepContent()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
